import { Children, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement, type ReactNode, type SelectHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../cx';
import { Icon } from './Icon';

type Opt = { value: string; label: string; disabled?: boolean; group?: string };

/** Lê `<option>` e `<optgroup>` filhos, para o Select personalizado manter a mesma API do nativo. */
function readOptions(children: ReactNode): Opt[] {
  const out: Opt[] = [];
  const text = (n: ReactNode): string => (Array.isArray(n) ? n.map(text).join('') : n == null || typeof n === 'boolean' ? '' : typeof n === 'object' && 'props' in (n as ReactElement) ? text(((n as ReactElement<{ children?: ReactNode }>).props).children) : String(n));
  const walk = (nodes: ReactNode, group?: string) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const el = child as ReactElement<{ value?: string | number; disabled?: boolean; label?: string; children?: ReactNode }>;
      if (el.type === 'optgroup') { walk(el.props.children, el.props.label); return; }
      if (el.type === 'option') { out.push({ value: String(el.props.value ?? text(el.props.children)), label: text(el.props.children).trim(), disabled: el.props.disabled, group }); return; }
      if (el.props.children) walk(el.props.children, group); // fragments / arrays
    });
  };
  walk(children);
  return out;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'defaultValue' | 'size'> {
  value?: string | number;
  defaultValue?: string | number;
  /** mesma assinatura do nativo: `e.target.value` */
  onChange?: (e: { target: { value: string; name?: string } }) => void;
  /** busca dentro da lista; automática a partir de 8 opções */
  searchable?: boolean;
  /** texto quando nenhuma opção casa com o valor */
  placeholder?: string;
  style?: CSSProperties;
}

/**
 * Select personalizado: mesma API do `<select>` (value, onChange com e.target.value, <option>/<optgroup>),
 * menu próprio com teclado, e busca quando a lista é longa. O nativo continua em `NativeSelect` para casos especiais.
 */
export function Select({ value, defaultValue, onChange, children, className, style, id, name, disabled, required, title, searchable, placeholder, autoFocus, 'aria-label': ariaLabel }: SelectProps) {
  const options = useMemo(() => readOptions(children), [children]);
  const controlled = value !== undefined;
  const [inner, setInner] = useState(String(defaultValue ?? options[0]?.value ?? ''));
  const current = controlled ? String(value) : inner;
  const selected = options.find((o) => o.value === current);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();
  const withSearch = searchable ?? options.length >= 8;
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const visible = useMemo(() => (query ? options.filter((o) => norm(o.label).includes(norm(query))) : options), [options, query]);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuH = Math.min(320, 44 + visible.length * 36 + (withSearch ? 44 : 0));
    const up = r.bottom + menuH > window.innerHeight - 8 && r.top > menuH;
    setPos({ top: up ? r.top - 4 : r.bottom + 4, left: r.left, width: Math.max(r.width, 180), up });
  };
  useLayoutEffect(() => { if (open) place(); }, [open, visible.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) close(); };
    const onMove = () => place();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    if (withSearch) setTimeout(() => searchRef.current?.focus(), 0);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { const i = visible.findIndex((o) => o.value === current); setActive(i >= 0 ? i : 0); } }, [open, query]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { menuRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const close = () => { setOpen(false); setQuery(''); };
  const pick = (o: Opt) => { if (o.disabled) return; if (!controlled) setInner(o.value); onChange?.({ target: { value: o.value, name } }); close(); triggerRef.current?.focus(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (!open) { if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOpen(true); } return; }
    if (e.key === 'Escape') { e.preventDefault(); close(); triggerRef.current?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(visible.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(visible.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); const o = visible[active]; if (o) pick(o); }
    else if (e.key === 'Tab') close();
    else if (!withSearch && e.key.length === 1) { const i = options.findIndex((o) => norm(o.label).startsWith(norm(e.key))); if (i >= 0) { setActive(i); } }
  };

  let lastGroup: string | undefined;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        title={title}
        aria-label={ariaLabel}
        className={cx('rr-select', open && 'rr-select--open', !selected && 'rr-select--empty', className)}
        style={style}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        autoFocus={autoFocus}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKey}
      >
        <span className="rr-select__value">{selected?.label ?? placeholder ?? options.find((o) => o.value === '')?.label ?? ''}</span>
        <Icon name="chevron-down" size={14} className="rr-select__chevron" />
      </button>
      {/* o valor segue para formulários nativos e para `required` */}
      {name && <input type="hidden" name={name} value={current} required={required} />}
      {open && pos && createPortal(
        <div ref={menuRef} className={cx('rr-select__menu', pos.up && 'rr-select__menu--up')} style={{ top: pos.top, left: pos.left, minWidth: pos.width, transform: pos.up ? 'translateY(-100%)' : undefined }} role="presentation" onKeyDown={onKey}>
          {withSearch && (
            <div className="rr-select__search">
              <Icon name="search" size={14} />
              <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="buscar…" aria-label="buscar na lista" />
            </div>
          )}
          <ul id={listId} role="listbox" className="rr-select__list" aria-activedescendant={visible[active] ? `${listId}-${active}` : undefined}>
            {visible.length === 0 && <li className="rr-select__empty">nada com “{query}”</li>}
            {visible.map((o, i) => {
              const head = o.group && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group;
              return (
                <li key={`${o.value}-${i}`}>
                  {head && <div className="rr-select__group">{head}</div>}
                  <div id={`${listId}-${i}`} data-i={i} role="option" aria-selected={o.value === current} aria-disabled={o.disabled} className={cx('rr-select__option', i === active && 'rr-select__option--active', o.value === current && 'rr-select__option--selected', o.disabled && 'rr-select__option--disabled')} onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)}>
                    <span>{o.label || <em className="rr-faint">vazio</em>}</span>
                    {o.value === current && <Icon name="check" size={14} />}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}

/** O `<select>` do navegador, para quando o personalizado não cabe (células muito densas, impressão). */
export function NativeSelect({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx('rr-select', 'rr-select--native', className)} {...rest} />;
}
